//TODO - TYPES FIX ESPECIALLY FOR COLLECTIONS
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { ApplicationState } from '../../types/state/storeTypes';
import { SubscriptionDetail } from '../../types/api/SubscriptionTypes';
import { User } from '../../types/api/UsersTypes';
import {
  SidebarProps,
  SidebarHeaderProps,
  LicenseSectionProps,
  PricingSectionProps,
  ActionsSectionProps,
  SidebarStateProps
} from '../../types/components/sidebar/SidebarTypes';

import { checkLegitimateOwnership, checkLicenseeProfile } from '../../helpers/licenseUtils';
import nonSubscriptionPurchaseHelpers from '../../helpers/nonSubscriptionPurchase';

import { Contributor } from '../shared/Contributor';
import MiniLicenseProfile from '../shared/MiniLicenseProfile';
import PriceBlock from '../collections/PriceBlock';
import CollectionOwnership from '../collections/CollectionOwnership';
import AddToWishlistButton from '../wishlists/AddToWishlistButton';
import InactiveCollectionMarker from '../shared/InactiveCollectionMarker';
import { BlackFridayButton } from '../shared/BlackFridayButton';
import { DividerWithText } from '../../modules/DividerWithText';
import NonSubPriceBlock from '../collections/NonSubPriceBlock';
import { AssetCountIcon } from '../../modules/Icons';
import PermanentPurchaseOptionsModal from '../permanent-purchase-options-modal/permanentPurchaseOptionsModal';
import EssentialsProductMarker from '../shared/EssentialsProductMarker';

export const SidebarHeader: React.FC<SidebarHeaderProps> = ({ collection }) => (
  <div cy-test-id="sidebar-header">
    <p cy-test-id="collection-name" className="text-white font-[Inter] text-24 md:text-40 leading-44 font-bold">
      {collection.name}
    </p>

    {collection.contributor ? (
      <Contributor
        username={collection.contributor.username}
        name={collection.contributor.name}
        mobile={false}
        assetCount={collection.elements}
      />
    ) : (
      <div className='flex items-center gap-1 mt-2'>
        <AssetCountIcon className='fill-[#7F898D] w-[16px] h-[16px]' />
        <p className='text-white text-[16px] font-light ml-1'>{collection.elements} assets</p>
      </div>
    )}
  </div>
);

export const LicenseSection: React.FC<LicenseSectionProps> = ({ setLicenseSelectionAssistDialogOpen, collectionOwned }) => {
  return (
    <div cy-test-id="license-section">
      {
        <MiniLicenseProfile
          setLicenseSelectionAssistDialogOpen={setLicenseSelectionAssistDialogOpen}
          collectionOwned={collectionOwned}
        />
      }
    </div>
  )
}

export const PricingSection: React.FC<PricingSectionProps> = ({
  collection,
  collectionOwned,
  ownership,
  products,
  setCollectionOwned,
  setOwnership,
  subscription,
  totalCreditPrice,
  setCollection,
}) => {
  const { shouldHideAddToCart } = nonSubscriptionPurchaseHelpers;

  const [disableDownloadButton, setDisableDownloadButton] = useState<boolean>(false);
  const [nullLicenseError, setNullLicenseError] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showPricing, setShowPricing] = useState<boolean>();
  const [showOwnership, setShowOwnership] = useState<boolean>();

  useEffect(() => {
    const hasNonLegitimateLicense = checkLegitimateOwnership(ownership);
    setDisableDownloadButton(hasNonLegitimateLicense);

    const hasLicenseeProfile = checkLicenseeProfile(ownership);
    setNullLicenseError(hasLicenseeProfile);
  }, [ownership]);

  useEffect(() => {
    const shouldShowPricing = collection?.free_for_member && !collection?.fully_purchased || !collectionOwned || (disableDownloadButton && nullLicenseError);
    const shouldShowOwnership = !shouldShowPricing || collection?.free_for_member;
    setShowPricing(shouldShowPricing);
    setShowOwnership(shouldShowOwnership);
  }, [
    collection,
    collectionOwned,
    disableDownloadButton,
    nullLicenseError,
  ]);

  if (!collection.active) return null;

  const handlePurchaseOptionsClick = () => {
    setIsOpen(true);
  };

  const onClose = () => setIsOpen(false)

  return (
    <div cy-test-id="pricing-section">
      {collection.membership_tier === 'silver' && (
        <div className="mt-10">
          <EssentialsProductMarker essentialsMonthlyExpiryDate={collection.ec_schedule_end_at}/>
        </div>
      )}
      {showPricing && (
        <>
          <PriceBlock
            purchasedItem={collection}
            className="mt-10"
            onPurchased={() => setCollectionOwned(true)}
            onOwnershipDataLoaded={setOwnership}
            totalCreditPrice={totalCreditPrice}
          />

          {!shouldHideAddToCart(collection, subscription) && (
            <>
              <DividerWithText />
              <NonSubPriceBlock
                collection={collection}
                className="mt-[20px]"
                onPurchased={() => setCollectionOwned(true)}
                onOwnershipDataLoaded={setOwnership}
              />
            </>
          )}

          {subscription && shouldHideAddToCart(collection, subscription) && (
            <div onClick={handlePurchaseOptionsClick} className='font-light text-center underline capitalize cursor-pointer text-a-blue'>Permanent Purchase Options</div>
          )}

          <PermanentPurchaseOptionsModal isOpen={isOpen} onClose={onClose} collection={collection} setCollection={setCollection}/>
        </>
      )}
      {showOwnership && (
        <CollectionOwnership
          collection={collection}
          ownership={ownership}
          products={products}
        />
      )}
    </div>
  );
};

export const ActionsSection: React.FC<ActionsSectionProps> = ({
  collection,
  subscription,
  auth,
  setIsOpen
}) => {
  const buttonClasses = 'rounded-[5px] border px-5 py-1 flex align-middle items-center gap-2 text-[13px] justify-center';
  const hoverClasses = 'hover:text-white hover:border-white hover:brightness-[80%] hover:stroke-white';
  const wishlistButtonClasses = `${buttonClasses} ${hoverClasses} stroke-a-light-gray text-a-light-gray border-[#4D5457] w-full !h-[40px] mt-[24px]`;

  return (
    <div cy-test-id="action-section">
      {collection.active && (
        <AddToWishlistButton
          type="button"
          product={collection}
          buttonClassName={wishlistButtonClasses}
          setIsOpen={setIsOpen}
          component="collection-page-sidebar"
        />
      )}

      <InactiveCollectionMarker cy-test-id="inactive-collection-marker" collection={collection} />

      {auth.authenticated !== undefined && (!subscription || !subscription.active) && (
        <BlackFridayButton type="collectionsPage" />
      )}
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = (props) => {
  const { collection, licenseeProfile, collectionOwned, setCollectionOwned, setOwnership, ownership, products, setLicenseSelectionAssistDialogOpen, user, subscription, setIsOpen, auth, totalCreditPrice, setCollection } = props;

  return (
    <div cy-test-id="sidebar" className="flex bg-[#1C1F20] pt-16">
      <div
        className={`hidden md:block sticky top-[5rem] !cursor-auto overflow-y-auto scrollbar-custom h-[calc(100vh-7rem)] min-w-[380px] lg:w-[400px] 2xl:w-[440px] px-9 pb-16`}>
        <SidebarHeader collection={collection} />

        <LicenseSection collection={collection} licenseeProfile={licenseeProfile} setLicenseSelectionAssistDialogOpen={setLicenseSelectionAssistDialogOpen} user={user} collectionOwned={collectionOwned}/>

        <PricingSection
          collection={collection}
          collectionOwned={collectionOwned}
          ownership={ownership}
          products={products}
          user={user}
          subscription={subscription}
          setCollectionOwned={setCollectionOwned}
          setOwnership={setOwnership}
          totalCreditPrice={totalCreditPrice}
          setCollection={setCollection}
        />

        <ActionsSection
          collection={collection}
          subscription={subscription}
          auth={auth}
          setIsOpen={setIsOpen}
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state: ApplicationState): SidebarStateProps => ({
  subscription: state.auth.subscription as SubscriptionDetail,
  user: state.auth.user as User,
});

export default connect(mapStateToProps)(Sidebar);
